import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import courseService from '../../../../services/mentor/courseService';
import enrollmentService from '../../../../services/enrollmentService';
import mentorService from '../../../../services/mentorService';
import batchService from '../../../../services/mentor/batchService';
import type { ICourse } from '../../../../types/course';
import type { IPopulatedEnrollment } from '../../../../types/enrollment';
import type { User } from '../../../../types/user';
import type { IBatch } from '../../../../types/batch';
import Loader from '../../../../components/common/Loader';
import { showToast } from '../../../../utils/showToast';
import { Plus, Trash2, CheckCircle, XCircle } from 'lucide-react';

const ManageCourseEnrollments: React.FC = () => {
  const { user, isLoading: isAuthLoading } = useAuth();
  const [courses, setCourses] = useState<ICourse[]>([]);
  const [selectedCourseId, setSelectedCourseId] = useState<string>('');
  const [enrollments, setEnrollments] = useState<IPopulatedEnrollment[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState<string | null>(null);
  const [allStudents, setAllStudents] = useState<User[]>([]);
  const [availableBatches, setAvailableBatches] = useState<IBatch[]>([]);
  const [studentToEnroll, setStudentToEnroll] = useState<string>('');
  const [batchToEnroll, setBatchToEnroll] = useState<string>('');

  const fetchEnrollmentsAndBatches = async () => {
    if (!selectedCourseId) {
      setEnrollments([]);
      setAvailableBatches([]);
      return;
    }
    setIsLoading(true);
    try {
      const [enrollmentsData, batchesData] = await Promise.all([
        enrollmentService.getEnrollmentsByCourse(selectedCourseId),
        batchService.getBatchesByCourse(selectedCourseId),
      ]);
      setEnrollments(enrollmentsData);
      setAvailableBatches(batchesData);
    } catch (err: any) {
      showToast(err.message, { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      setIsLoading(true);
      try {
        const [coursesData, studentsData] = await Promise.all([
          courseService.getAllCoursesForAdmin(),
          mentorService.getAllStudents(),
        ]);
        setCourses(coursesData);
        setAllStudents(studentsData);
      } catch (err: any) {
        showToast(err.message, { type: 'error' });
      } finally {
        setIsLoading(false);
      }
    };
    if (user) fetchInitialData();
  }, [user]);

  useEffect(() => {
    if (!isAuthLoading && user) {
      fetchEnrollmentsAndBatches();
    }
  }, [selectedCourseId, isAuthLoading, user]);

  const handleEnrollStudent = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!studentToEnroll || !batchToEnroll) {
      return showToast('Please select a student and a batch.', { type: 'info' });
    }
    setIsProcessing('enroll-student');
    try {
      await enrollmentService.enrollStudent({
        studentId: studentToEnroll,
        courseId: selectedCourseId,
        batchId: batchToEnroll,
      });
      showToast('Student enrolled successfully!', { type: 'success' });
      fetchEnrollmentsAndBatches();
      setStudentToEnroll('');
      setBatchToEnroll('');
    } catch (err: any) {
      showToast(err.message, { type: 'error' });
    } finally {
      setIsProcessing(null);
    }
  };

  const handleDeEnroll = async (enrollmentId: string, studentName: string) => {
    if (!window.confirm(`Are you sure you want to de-enroll ${studentName}?`)) return;
    setIsProcessing(enrollmentId);
    try {
      const message = await enrollmentService.deleteEnrollment(enrollmentId);
      showToast(message, { type: 'success' });
      setEnrollments(prev => prev.filter(e => e._id !== enrollmentId));
    } catch (err: any) {
      showToast(err.message, { type: 'error' });
    } finally {
      setIsProcessing(null);
    }
  };

  const handleUpdateCompletionStatus = async (enrollment: IPopulatedEnrollment) => {
    const newStatus = enrollment.completionStatus === 'in-progress' ? 'completed' : 'in-progress';
    if (!window.confirm(`Change status to '${newStatus}'?`)) return;
    setIsProcessing(enrollment._id);
    try {
      await enrollmentService.updateEnrollmentStatus(enrollment._id, newStatus);
      showToast('Status updated successfully!', { type: 'success' });
      fetchEnrollmentsAndBatches();
    } catch (err: any) {
      showToast(err.message, { type: 'error' });
    } finally {
      setIsProcessing(null);
    }
  };

  if (isAuthLoading) return <Loader />;

  return (
    <div className="py-10 px-4">
      <div className="max-w-7xl mx-auto space-y-6">
        <h1 className="text-2xl font-bold">Manage Course Enrollments</h1>
        <div>
          <label htmlFor="courseSelect" className="block text-sm font-medium">Select a Course</label>
          <select id="courseSelect" value={selectedCourseId} onChange={(e) => setSelectedCourseId(e.target.value)} className="w-full px-3 py-2 border rounded-md">
            <option value="">-- Select Course --</option>
            {courses.map(course => <option key={course._id} value={course._id}>{course.title}</option>)}
          </select>
        </div>

        {selectedCourseId && (
          <div className="p-4 bg-gray-50 rounded-lg border">
            <h2 className="text-lg font-semibold mb-2">Enroll New Student</h2>
            <form onSubmit={handleEnrollStudent} className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
              <div className="md:col-span-1">
                <label htmlFor="studentSelect" className="block text-sm font-medium">Student</label>
                <select id="studentSelect" value={studentToEnroll} onChange={(e) => setStudentToEnroll(e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-md" disabled={!!isProcessing}>
                  <option value="">-- Select Student --</option>
                  {allStudents.map(student => <option key={student._id} value={student._id}>{student.name}</option>)}
                </select>
              </div>
              <div className="md:col-span-1">
                <label htmlFor="batchSelect" className="block text-sm font-medium">Batch</label>
                <select id="batchSelect" value={batchToEnroll} onChange={(e) => setBatchToEnroll(e.target.value)} className="w-full mt-1 px-3 py-2 border rounded-md" disabled={!selectedCourseId || !!isProcessing}>
                  <option value="">-- Select Batch --</option>
                  {availableBatches.map(batch => <option key={batch._id} value={batch._id}>{batch.name}</option>)}
                </select>
              </div>
              <button type="submit" disabled={!!isProcessing} className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center">
                <Plus className="w-4 h-4 mr-1"/> Enroll
              </button>
            </form>
          </div>
        )}

        {isLoading ? <Loader /> : (
          <>
            {!selectedCourseId ? <p className="text-center text-gray-500">Please select a course to view enrollments.</p> : enrollments.length === 0 ? <p className="text-center text-gray-500">No students enrolled in this course yet.</p> : (
              <div className="overflow-x-auto bg-white rounded-lg shadow">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase">Student</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase">Batch</th>
                      <th className="px-6 py-3 text-left text-xs font-medium uppercase">Status</th>
                      <th className="px-6 py-3 text-right text-xs font-medium uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {enrollments.map(enrollment => {
                      const student = enrollment.student && typeof enrollment.student === 'object'
                        ? enrollment.student
                        : null;

                      if (!student) {
                        return null;
                      }

                      return (
                        <tr key={enrollment._id}>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900">{student.name}</div>
                            <div className="text-sm text-gray-500">{student.email}</div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">{enrollment.batch ? enrollment.batch.name : 'Unassigned'}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2 inline-flex text-xs font-semibold rounded-full ${enrollment.completionStatus === 'completed' ? 'bg-green-100 text-green-800' : 'bg-blue-100 text-blue-800'}`}>
                              {enrollment.completionStatus.replace('-', ' ')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium space-x-4">
                            <button onClick={() => handleUpdateCompletionStatus(enrollment)} disabled={isProcessing === enrollment._id} className="text-indigo-600 hover:text-indigo-900 disabled:opacity-50">
                              {enrollment.completionStatus === 'in-progress' ? <CheckCircle className="h-4 w-4 inline mr-1" /> : <XCircle className="h-4 w-4 inline mr-1" />}
                              {enrollment.completionStatus === 'in-progress' ? 'Mark Complete' : 'Mark In-Progress'}
                            </button>
                            <button onClick={() => handleDeEnroll(enrollment._id, student.name)} disabled={isProcessing === enrollment._id} className="text-orange-600 hover:text-orange-900 disabled:opacity-50">
                              <Trash2 className="h-4 w-4 inline mr-1" />De-enroll
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ManageCourseEnrollments;